import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';

/**
 * MarqueeContainer - 재사용 가능한 스크롤 기반 마퀴 컨테이너
 * @param {React.ReactNode} children - 마퀴 효과를 적용할 자식 요소들
 * @param {number} [speed=0.6] - 스크롤 1px 당 가로 이동 픽셀
 * @param {number} [duplicate=3] - 반복 복제 횟수
 * @param {number} [height] - 컨테이너 높이 (px)
 * @param {object} [sx] - 추가 스타일링
 * @param {function} [onVisibleItemsChange] - 보이는 아이템 변경 시 콜백
 * @param {boolean} [reverse=false] - 역방향 스크롤 여부
 */
const MarqueeContainer = ({ 
  children, 
  speed = 0.6, 
  duplicate = 3, 
  height = 180,
  gap = 24,
  sx = {},
  onVisibleItemsChange,
  reverse = false
}) => {
  const wrapRef = useRef(null);
  const groupRef = useRef(null);
  const lastYRef = useRef(typeof window !== 'undefined' ? window.scrollY : 0);
  const offsetRef = useRef(0);
  const rafRef = useRef(0);
  const [groupWidth, setGroupWidth] = useState(0);
  const [wrapWidth, setWrapWidth] = useState(0);

  // children을 배열로 변환
  const childrenArray = useMemo(() => {
    return React.Children.toArray(children).filter(Boolean);
  }, [children]);

  // 복제된 children 리스트 생성
  const duplicatedChildren = useMemo(() => {
    if (childrenArray.length === 0) return [];
    
    const times = Math.max(2, duplicate);
    const repeated = [];
    for (let i = 0; i < times; i++) {
      repeated.push(...childrenArray);
    }
    return repeated;
  }, [childrenArray, duplicate]);

  // 그룹 폭과 래퍼 폭 측정
  useEffect(() => {
    const measure = () => {
      if (groupRef.current) {
        const w = groupRef.current.scrollWidth;
        setGroupWidth(w / Math.max(2, duplicate));
      }
      if (wrapRef.current) {
        setWrapWidth(wrapRef.current.clientWidth || 0);
      }
    };

    measure();
    const ro = new ResizeObserver(measure);
    if (groupRef.current) ro.observe(groupRef.current);
    if (wrapRef.current) ro.observe(wrapRef.current);
    
    return () => ro.disconnect();
  }, [duplicate, duplicatedChildren.length]);

  // 스크롤 이벤트 처리
  useEffect(() => {
    const onScroll = () => {
      const cur = window.scrollY;
      const last = lastYRef.current;
      const dy = cur - last; // 내려가면 +, 올라가면 -
      lastYRef.current = cur;
      const direction = reverse ? -1 : 1;
      offsetRef.current += dy * speed * direction;
      
      if (!rafRef.current) {
        rafRef.current = requestAnimationFrame(() => {
          rafRef.current = 0;
          // transform만 업데이트하면 되므로 직접 DOM 조작
          if (groupRef.current && groupWidth > 0) {
            let x = offsetRef.current % groupWidth;
            if (x < 0) x += groupWidth;
            const normalizedX = reverse ? (wrapWidth - x) : -x; // reverse일 때 오른쪽에서, 기본은 왼쪽에서
            groupRef.current.style.transform = `translate3d(${normalizedX}px, 0, 0)`;
            
            // 보이는 아이템 변경 콜백
            if (onVisibleItemsChange) {
              const visibleOffset = Math.abs(normalizedX);
              onVisibleItemsChange(visibleOffset);
            }
          }
        });
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [speed, groupWidth, onVisibleItemsChange, reverse, wrapWidth]);

  // 초기 렌더링용 transform 값
  let initialX = 0;
  if (groupWidth > 0) {
    initialX = offsetRef.current % groupWidth;
    if (initialX < 0) initialX += groupWidth;
    if (reverse) {
      // reverse일 때는 오른쪽에서 시작
      initialX = wrapWidth - initialX;
    } else {
      // 기본값: 왼쪽에서 시작
      initialX = -initialX;
    }
  }

  // 데이터 없을 때 처리
  if (childrenArray.length === 0) return null;

  return (
    <Box 
      ref={wrapRef} 
      sx={{ 
        width: '100%',
        height: height,
        overflow: 'hidden',
        position: 'relative',
        contain: 'strict',
        ...sx
      }}
    >
      <Box
        ref={groupRef}
        sx={{
          display: 'flex',
          gap: `${gap}px`,
          transform: `translate3d(${initialX}px, 0, 0)`,
          willChange: 'transform',
          position: 'absolute',
          top: 0,
          left: 0,
          height: '100%',
        }}
      >
        {duplicatedChildren.map((child, idx) => 
          React.cloneElement(child, { 
            key: `marquee-item-${idx}` 
          })
        )}
      </Box>
    </Box>
  );
};

export default MarqueeContainer;